/*
 * /auth/practitioner-profile — practitioner self-service: profile + signature
 */
import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	cn,
	Container,
	Divider,
	Form,
	InputFormField,
	Page,
	PageHeader,
	Placeholder,
	SelectFormField,
	TextareaFormField,
	toast,
} from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { Loader2, Pen, User } from "lucide-react"
import { useState } from "react"
import { z } from "zod"
import { trpc } from "../../trpc"
import { rolePageLoader } from "../../utils/auth.utils"
import { SignaturePad } from "../../features/patient-portal/signature-pad"

export const Route = createFileRoute("/auth/practitioner-profile")({
	component: PractitionerProfilePage,
	loader: rolePageLoader(["practitioner", "nurse", "admin", "super_admin"]),
})

type Tab = "profile" | "signature"

const APPT_LENGTH_OPTIONS = [
	{ value: "15", label: "15 min" },
	{ value: "20", label: "20 min" },
	{ value: "30", label: "30 min" },
	{ value: "45", label: "45 min" },
	{ value: "60", label: "60 min" },
	{ value: "90", label: "90 min" },
	{ value: "120", label: "120 min" },
]

const profileSchema = z.object({
	firstName: z.string().min(1, "Required"),
	middleName: z.string().optional(),
	lastName: z.string().min(1, "Required"),
	clinicName: z.string().optional(),
	professionalDesignation: z.string().optional(),
	specialization: z.string().optional(),
	defaultAppointmentMinutes: z.string().optional(),
	email: z.string().optional(),
	phone: z.string().optional(),
	phone2: z.string().optional(),
	fax: z.string().optional(),
	website: z.string().optional(),
	biography: z.string().optional(),
})

type ProfileValues = z.infer<typeof profileSchema>

function PractitionerProfilePage() {
	const [activeTab, setActiveTab] = useState<Tab>("profile")
	const utils = trpc.useUtils()

	const { data: practitioner, isLoading } = trpc.practitioner.getByFilter.useQuery({
		where: {},
	})

	const update = trpc.practitioner.update.useMutation({
		onSuccess: () => {
			utils.practitioner.getByFilter.invalidate()
			utils.practitioner.getMyPractitioner.invalidate()
			toast.success("Profile updated")
		},
		onError: (err) => toast.error(err.message),
	})

	const saveSignature = trpc.practitioner.saveMySignature.useMutation({
		onSuccess: () => {
			utils.practitioner.getByFilter.invalidate()
			utils.practitioner.getMyPractitioner.invalidate()
			toast.success("Signature saved")
		},
		onError: (err) => toast.error(err.message),
	})

	async function handleProfileSubmit(values: ProfileValues) {
		if (!practitioner) return
		try {
			await update.mutateAsync({
				id: practitioner.id,
				...values,
				middleName: values.middleName || null,
				clinicName: (values as any).clinicName || null,
				professionalDesignation: values.professionalDesignation || null,
				specialization: values.specialization || null,
				email: values.email || null,
				phone: values.phone || null,
				phone2: values.phone2 || null,
				fax: values.fax || null,
				website: values.website || null,
				biography: values.biography || null,
				defaultAppointmentMinutes: values.defaultAppointmentMinutes
					? parseInt(values.defaultAppointmentMinutes, 10)
					: null,
			} as any)
		} catch (e: any) {
			return e?.message ?? "An error occurred"
		}
	}

	if (isLoading) {
		return (
			<Page>
				<Container>
					<Placeholder title="Loading your profile…" />
				</Container>
			</Page>
		)
	}

	if (!practitioner) {
		return (
			<Page>
				<Container>
					<Placeholder title="No practitioner record found. Contact an administrator to set up your profile." />
				</Container>
			</Page>
		)
	}

	const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
		{ id: "profile", label: "My Profile", icon: User },
		{ id: "signature", label: "Signature", icon: Pen },
	]

	return (
		<Page centerVertical={false}>
			<PageHeader
				title={`${practitioner.firstName} ${practitioner.lastName}${(practitioner as any).professionalDesignation ? ` · ${(practitioner as any).professionalDesignation}` : ""}`}
			/>
			<Container width="lg" padding="lg" gap="lg">
				{/* Tab bar */}
				<div className="flex gap-1 border-b border-border">
					{tabs.map(({ id, label, icon: Icon }) => (
						<button
							key={id}
							type="button"
							onClick={() => setActiveTab(id)}
							className={cn(
								"flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors",
								activeTab === id
									? "border-primary text-primary"
									: "border-transparent text-muted-foreground hover:text-foreground",
							)}
						>
							<Icon className="size-4" />
							{label}
						</button>
					))}
				</div>

				{/* Profile tab */}
				{activeTab === "profile" && (
					<Card>
						<CardHeader>
							<CardTitle>Profile Details</CardTitle>
						</CardHeader>
						<CardContent>
							<Form<ProfileValues>
								schema={profileSchema}
								defaultValues={{
									firstName: practitioner.firstName ?? "",
									middleName: practitioner.middleName ?? "",
									lastName: practitioner.lastName ?? "",
									clinicName: (practitioner as any).clinicName ?? "",
									professionalDesignation: practitioner.professionalDesignation ?? "",
									specialization: practitioner.specialization ?? "",
									defaultAppointmentMinutes: practitioner.defaultAppointmentMinutes
										? String(practitioner.defaultAppointmentMinutes)
										: "",
									email: practitioner.email ?? "",
									phone: practitioner.phone ?? "",
									phone2: practitioner.phone2 ?? "",
									fax: practitioner.fax ?? "",
									website: practitioner.website ?? "",
									biography: practitioner.biography ?? "",
								}}
								onSubmit={handleProfileSubmit}
								className="flex flex-col gap-5"
							>
								<section>
									<p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
										Name
									</p>
									<div className="grid grid-cols-3 gap-3">
										<InputFormField name="firstName" label="First Name" placeholder="Jane" />
										<InputFormField name="middleName" label="Middle Name" placeholder="A." />
										<InputFormField name="lastName" label="Last Name" placeholder="Smith" />
									</div>
								</section>

								<Divider />

								<section>
									<p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
										Professional
									</p>
									<div className="grid grid-cols-3 gap-3">
										<InputFormField
											name="professionalDesignation"
											label="Designation"
											placeholder="MD, NP, RN…"
										/>
										<InputFormField
											name="specialization"
											label="Specialization"
											placeholder="General Practice"
										/>
										<SelectFormField
											name="defaultAppointmentMinutes"
											label="Default Appt Length"
											options={APPT_LENGTH_OPTIONS}
											placeholder="Select…"
										/>
									</div>
									<div className="mt-3">
										<InputFormField
											name="clinicName"
											label="Clinic / Practice Name"
											placeholder="e.g. Valley Medical Centre"
										/>
									</div>
								</section>

								<Divider />

								<section>
									<p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
										Contact
									</p>
									<div className="grid grid-cols-2 gap-3">
										<InputFormField name="email" label="Email" placeholder="jane@clinic.com" />
										<InputFormField name="phone" label="Phone" placeholder="+1 (555) 000-0000" />
										<InputFormField name="phone2" label="Phone 2" placeholder="+1 (555) 000-0001" />
										<InputFormField name="fax" label="Fax" placeholder="+1 (555) 000-0002" />
										<InputFormField
											name="website"
											label="Website"
											placeholder="https://clinic.com"
											className="col-span-2"
										/>
									</div>
								</section>

								<Divider />

								<section>
									<p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
										Biography
									</p>
									<TextareaFormField
										name="biography"
										label=""
										placeholder="Short professional biography…"
										rows={3}
									/>
								</section>

								<Button type="submit" disabled={update.isPending} className="self-start">
									{update.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
									Save Changes
								</Button>
							</Form>
						</CardContent>
					</Card>
				)}

				{/* Signature tab */}
				{activeTab === "signature" && (
					<Card>
						<CardHeader>
							<CardTitle>My Signature</CardTitle>
						</CardHeader>
						<CardContent className="flex flex-col gap-4">
							{(practitioner as any).signatureData && (
								<div className="flex flex-col gap-2">
									<p className="text-sm font-medium text-muted-foreground">
										Current signature on file
									</p>
									<img
										src={(practitioner as any).signatureData}
										alt="Current signature"
										className="max-h-24 rounded-md border border-border bg-white p-2 object-contain"
									/>
									<p className="text-xs text-muted-foreground">
										Draw a new signature below to replace it.
									</p>
								</div>
							)}
							{!(practitioner as any).signatureData && (
								<p className="text-sm text-muted-foreground">
									No signature on file. Draw one below — it will be used to sign LP registration forms.
								</p>
							)}
							<SignaturePad
								onSave={(dataUrl) =>
									saveSignature.mutate({ signatureData: dataUrl })
								}
							/>
						</CardContent>
					</Card>
				)}
			</Container>
		</Page>
	)
}
